import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import Select from "react-select";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import CommonTableForCandidate from "components/Common/CandidateTableContainer";
import LegacyFilterToolbar, {
  LEGACY_FILTER_INPUT_CLASS,
} from "components/Common/LegacyFilterToolbar";
import {
  fetchCandidateFadvMetadata,
  submitCandidateFadvFilterData,
  triggerFadvApiCall,
} from "model/CandidateModel";
import { DEFAULT_PAGE_SIZE, mapApiMetadataToTableColumnDefinition } from "helpers/GeneralHelper";
import { AsyncClientSelect } from "components/Common/AsyncSearchSelects";
import {
  applyClientAsyncFilterOptionToFormState,
  buildClientAsyncFilterInitialOption,
  getClientAsyncFilterLabelFieldKey,
} from "helpers/metadataFilterHelpers";
import Loaders from "components/Common/Loaders";
import { usePermissionContext } from "libs/context/PermissionContext";
import { useCommonFilterOptions } from "libs/context/CommonFilterOptionsContext";
import { useCandidateTableSettingsPageSize } from "hooks/useSettingsReadyTablePageSize";


const CSV_STATUS_OPTIONS = [
  { value: "", label: "Status" },
  { value: "dcie", label: "dcie" },
  { value: "cnf", label: "cnf" },
  { value: "enf", label: "enf" },
  { value: "apie", label: "apie" },
  { value: "in_sent", label: "in_sent" },
  { value: "completed", label: "completed" },
  { value: "send", label: "send" },
];

const API_STATUS_OPTIONS = [
  { value: "", label: "API Status" },
  { value: "completed", label: "completed" },
  { value: "failed", label: "failed" },
  { value: "Sent", label: "Sent" },
  { value: "On", label: "On" },
];

const FADV_EXECUTION_TYPE_OPTIONS = [
  { value: 1, label: "Full" },
  { value: 2, label: "Order Status Only" },
];

const selectMenuStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menu: (base) => ({ ...base, zIndex: 9999 }),
};

const strOrNull = (v) => {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  return s === "" ? null : s;
};

const resolveRowId = (row = {}) =>
  String(row?.id ?? row?._id ?? row?.candidateId ?? "").trim();

const FadvOrderLog = () => {
  const { permissionData } = usePermissionContext() || {};
  const { fetchMetadataSchema } = useCommonFilterOptions();
  const { pageSize: settingsPageSize, settingsReady } =
    useCandidateTableSettingsPageSize("fadv-order-log");
  const initialFilterFetchedRef = useRef(false);
  const [metadataBootstrapDone, setMetadataBootstrapDone] = useState(false);
  const [filterForm, setFilterForm] = useState({
    search: "",
    clientId: "",
    [getClientAsyncFilterLabelFieldKey()]: "",
    status: "",
    apiStatus: "",
    fromDate: "",
    toDate: "",
  });
  const [loading, setLoading] = useState(true);
  const [tableMetaData, setTableMetaData] = useState([]);
  const [rows, setRows] = useState([]);
  const [requestPayload, setRequestPayload] = useState({
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [paginationMeta, setPaginationMeta] = useState({
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    totalPages: 0,
    totalRecords: 0,
  });
  const [fadvExecutionModalOpen, setFadvExecutionModalOpen] = useState(false);
  const [fadvExecutionRow, setFadvExecutionRow] = useState(null);
  const [selectedExecutionType, setSelectedExecutionType] = useState(
    FADV_EXECUTION_TYPE_OPTIONS[0]
  );
  const [triggeringFadv, setTriggeringFadv] = useState(false);

  const fadvOrderLogActionPermissions = useMemo(
    () => ({
      canTriggerFadv: Boolean(permissionData?.CallFadvAPIAccess),
      canChooseFadvExecutionType: Boolean(permissionData?.CallFadvFullExecutionAccess),
    }),
    [permissionData]
  );

  const hasAnyFadvOrderLogRowAction = useMemo(
    () => fadvOrderLogActionPermissions.canTriggerFadv,
    [fadvOrderLogActionPermissions]
  );

  const getList = async (payloadOverrides = {}) => {
    setLoading(true);
    try {
      const payload = {
        ...requestPayload,
        ...payloadOverrides,
      };
      const response = await submitCandidateFadvFilterData(payload);
      const responseData = response?.data || {};
      const list = responseData?.records ?? responseData?.data ?? responseData ?? [];
      const data = Array.isArray(list) ? list : [];
      setRows(
        data?.map((item) => ({
          ...item,
          id: item?.id || item?._id || "",
          // API now sends `downloadURL`; map it to `link` so table shows download action.
          url: item?.link || item?.downloadURL || item?.downloadUrl || "",
        }))
      );
      setPaginationMeta({
        pageNumber: Number(responseData?.pageNumber) || Number(payload?.pageNumber) || 1,
        pageSize: Number(responseData?.pageSize) || Number(payload?.pageSize) || DEFAULT_PAGE_SIZE,
        totalPages: Number(responseData?.totalPages) || 0,
        totalRecords: Number(responseData?.totalRecords) || data.length,
      });
      setRequestPayload(payload);
    } catch (e) {
      toast.error(e?.message || "Unable to load FADV order log");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const bootstrap = async () => {
      setLoading(true);
      try {
        const metaRes = await fetchMetadataSchema("metadata:candidateFadv", () =>
          fetchCandidateFadvMetadata({})
        );
        const meta = metaRes?.data ?? metaRes ?? {};
        const tableColumns = meta?.table?.columns?.length
          ? meta.table.columns.map((col) => mapApiMetadataToTableColumnDefinition(col))
          : [];
        setTableMetaData(tableColumns);
      } catch (e) {
        toast.error(e?.message || "Unable to load FADV order log metadata");
        setTableMetaData([]);
      }

      setMetadataBootstrapDone(true);
    };
    bootstrap();
  }, []);

  /** 1) settings page size ready → 2) call filter API once */
  useEffect(() => {
    if (!settingsReady || !metadataBootstrapDone || initialFilterFetchedRef.current) return;
    initialFilterFetchedRef.current = true;
    const pageSize = Number(settingsPageSize) || DEFAULT_PAGE_SIZE;
    setRequestPayload((previous) => ({ ...previous, pageSize }));
    setPaginationMeta((previous) => ({ ...previous, pageSize }));
    void getList({ pageNumber: 1, pageSize });
  }, [settingsReady, settingsPageSize, metadataBootstrapDone]);

  const handleFilterFieldChange = (e) => {
    const { name, value } = e.target;
    setFilterForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleClientSelect = (opt) => {
    setFilterForm((prev) => applyClientAsyncFilterOptionToFormState(prev, opt));
  };

  const buildSearchPayload = (form = filterForm) => {
    const f = form || {};
    return {
      pageNumber: 1,
      pageSize: requestPayload?.pageSize || DEFAULT_PAGE_SIZE,
      nameContains: strOrNull(f.search),
      search: strOrNull(f.search),
      name: strOrNull(f.search),
      clientId: strOrNull(f.clientId),
      status: strOrNull(f.status),
      csvStatus: strOrNull(f.status),
      apiStatus: strOrNull(f.apiStatus),
      fromDate: strOrNull(f.fromDate),
      toDate: strOrNull(f.toDate),
    };
  };

  const handleSearch = async () => {
    await getList(buildSearchPayload());
  };

  const handlePageChange = async (page) => {
    await getList({ pageNumber: page });
  };

  const handleRowsPerPageChange = async (rowsPerPage) => {
    await getList({ pageNumber: 1, pageSize: rowsPerPage });
  };

  const submitFadvTrigger = async (rowId, executionType) => {
    setTriggeringFadv(true);
    try {
      const response = await triggerFadvApiCall(rowId, { executionType });
      if (response?.success || response?.isSuccess) {
        toast.success(response?.message || "FADV API call triggered");
        setFadvExecutionModalOpen(false);
        setFadvExecutionRow(null);
        await getList(requestPayload);
        return;
      }
      toast.error(response?.message || "Unable to trigger FADV API call");
    } finally {
      setTriggeringFadv(false);
    }
  };

  const handleTriggerFadv = async (item) => {
    const rowId = resolveRowId(item);
    if (!rowId) {
      toast.error("Row ID not found");
      return;
    }

    if (fadvOrderLogActionPermissions.canChooseFadvExecutionType) {
      setFadvExecutionRow(item);
      setSelectedExecutionType(FADV_EXECUTION_TYPE_OPTIONS[0]);
      setFadvExecutionModalOpen(true);
      return;
    }

    const swalResult = await Swal.fire({
      title: "Are you sure you want to send this FADV API call?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Confirm",
      cancelButtonText: "Cancel",
      reverseButtons: true,
    });
    if (!swalResult?.isConfirmed) return;

    await submitFadvTrigger(rowId, 2);
  };

  const handleFadvExecutionModalSubmit = async () => {
    const rowId = resolveRowId(fadvExecutionRow);
    if (!rowId) {
      toast.error("Row ID not found");
      return;
    }
    const executionType = Number(selectedExecutionType?.value) || 2;
    await submitFadvTrigger(rowId, executionType);
  };

  const handleFadvExecutionModalClose = () => {
    if (triggeringFadv) return;
    setFadvExecutionModalOpen(false);
    setFadvExecutionRow(null);
  };

  const filteredData = useMemo(() => rows, [rows]);

  return (
    <div className="page-content">
      <Container fluid>
        <Card>
          <CardBody>
          <h5 className="fw-bold mb-3">FADV Order Log</h5>
            <hr />

                        <LegacyFilterToolbar>
            <Row className="metadata-filter-toolbar__row g-2 g-md-3 align-items-end">
              <Col xs="12" md="2" className="metadata-filter-field metadata-filter-field--text">
                <span className="manage-user-filter-label">Search</span>
                <div className="metadata-filter-field__control">
                <Input
                  type="text"
                  name="search"
                  placeholder="Search by Name"
                  className={LEGACY_FILTER_INPUT_CLASS}
                  value={filterForm.search}
                  onChange={handleFilterFieldChange}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSearch();
                  }}
                />
                </div>
              </Col>

              <Col xs="12" md="2" className="metadata-filter-field metadata-filter-field--autocomplete">
                <span className="manage-user-filter-label">Client</span>
                <div className="metadata-filter-field__control">
                <AsyncClientSelect
                  key={`fadv-order-log-client-${filterForm.clientId}`}
                  pageName="FadvLog"
                  value={filterForm.clientId}
                  initialOption={buildClientAsyncFilterInitialOption(
                    filterForm.clientId,
                    filterForm[getClientAsyncFilterLabelFieldKey()]
                  )}
                  onChange={handleClientSelect}
                  placeholder="Select Client"
                />
                </div>
              </Col>

              <Col xs="12" md="2" className="metadata-filter-field metadata-filter-field--dropdown">
                <span className="manage-user-filter-label">API Status</span>
                <div className="metadata-filter-field__control">
                <Input
                  type="select"
                  name="apiStatus"
                  value={filterForm.apiStatus}
                  onChange={handleFilterFieldChange}
                  className={LEGACY_FILTER_INPUT_CLASS}
                >
                  {API_STATUS_OPTIONS.map((o) => (
                    <option key={`api-${o.value}-${o.label}`} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Input>
                </div>
              </Col>

              <Col xs="12" md="2" className="metadata-filter-field metadata-filter-field--date">
                <span className="manage-user-filter-label">From date</span>
                <div className="metadata-filter-field__control">
                <Input
                  type="date"
                  name="fromDate"
                  value={filterForm.fromDate}
                  onChange={handleFilterFieldChange}
                  className={LEGACY_FILTER_INPUT_CLASS}
                />
                </div>
              </Col>

              <Col xs="12" md="2" className="metadata-filter-field metadata-filter-field--date">
                <span className="manage-user-filter-label">To date</span>
                <div className="metadata-filter-field__control">
                <Input
                  type="date"
                  name="toDate"
                  value={filterForm.toDate}
                  onChange={handleFilterFieldChange}
                  className={LEGACY_FILTER_INPUT_CLASS}
                />
                </div>
              </Col>
              <Col xs="12" md="2" className="metadata-filter-field metadata-filter-field--actions">
              <span className="manage-user-filter-label" aria-hidden="true">&nbsp;</span>
              <div className="metadata-filter-toolbar__actions">
              <Button color="primary" className="w-100 manage-user-search-btn" onClick={handleSearch}>
                  <i className="fa fa-search me-1" />
                  Search
                </Button>
              </div>
              </Col>
            </Row>
            </LegacyFilterToolbar>
            {loading ? (
              <Loaders />
            ) : tableMetaData?.length ? (
              <CommonTableForCandidate
                columns={tableMetaData}
                data={filteredData}
                isActionRequired={hasAnyFadvOrderLogRowAction}
                onTriggerFadv={
                  fadvOrderLogActionPermissions.canTriggerFadv
                    ? handleTriggerFadv
                    : undefined
                }
                pageName="fadv-order-log"
                isPagination={true}
                currentPage={paginationMeta?.pageNumber}
                rowsPerPage={paginationMeta?.pageSize}
                totalPages={paginationMeta?.totalPages}
                totalRecords={paginationMeta?.totalRecords}
                onPageChange={handlePageChange}
                onPageSizeChange={handleRowsPerPageChange}
              />
            ) : (
              <div className="text-muted text-center py-5">Unable to load table columns.</div>
            )}
          </CardBody>
        </Card>
      </Container>

      <Modal
        isOpen={fadvExecutionModalOpen}
        toggle={handleFadvExecutionModalClose}
        centered
      >
        <ModalHeader toggle={handleFadvExecutionModalClose}>
          FADV Execution Type
        </ModalHeader>
        <ModalBody>
          <Label className="fw-semibold mb-2">Select execution type</Label>
          <Select
            className="select2-selection"
            options={FADV_EXECUTION_TYPE_OPTIONS}
            value={selectedExecutionType}
            onChange={setSelectedExecutionType}
            placeholder="Select execution type"
            isSearchable={false}
            menuPortalTarget={typeof document !== "undefined" ? document.body : null}
            menuPosition="fixed"
            styles={selectMenuStyles}
          />
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            type="button"
            onClick={handleFadvExecutionModalClose}
            disabled={triggeringFadv}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            type="button"
            onClick={handleFadvExecutionModalSubmit}
            disabled={triggeringFadv || !selectedExecutionType}
          >
            {triggeringFadv ? <Spinner size="sm" /> : "Submit"}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

FadvOrderLog.propTypes = {
  orders: PropTypes.array,
  onGetOrders: PropTypes.func,
};

export default withRouter(FadvOrderLog);
